import type { Localized } from '@/i18n'

/**
 * The seven lights of "Ekspedisi 7 Cahaya".
 *
 * Each light is a movement with its own concrete deliverables. The seven
 * themes come from the programme team's initial draft; the summaries and
 * deliverables are a working proposal. While a pillar is `draft` the UI marks
 * it as such — set `status: 'final'` once the team signs off on its copy.
 */

export interface CahayaPillar {
  slug: string
  /** 1-7, drives the displayed numeral. */
  order: number
  /** "Cahaya Pertama" / "First Light", shown as the card eyebrow. */
  ordinal: Localized
  title: Localized
  summary: Localized
  deliverables: Localized<string[]>
  /**
   * The person and organisation named to lead this pillar. Names aren't
   * translated, so plain strings rather than `Localized` — `null` renders as
   * "to be announced" until the programme team confirms one.
   */
  leadPerson: string | null
  leadOrganization: string | null
  status: 'draft' | 'final'
}

export const CAHAYA_PILLARS: CahayaPillar[] = [
  {
    slug: 'understanding-time',
    order: 1,
    ordinal: { id: 'Cahaya Pertama', en: 'First Light' },
    title: { id: 'Memahami Waktu', en: 'Understanding Time' },
    summary: {
      id: 'Gerakan untuk membaca waktu dari langit — gerak Matahari dan Bulan, hisab dan rukyat, kalender Hijriah, hingga penentuan waktu salat — dengan gerhana sebagai buktinya yang paling presisi.',
      en: 'A movement to read time from the sky — the motion of the Sun and Moon, hisab and rukyat, the Hijri calendar, and prayer times — with the eclipse as its most precise proof.',
    },
    deliverables: {
      id: [
        'Modul publik tentang astronomi waktu, hisab-rukyat, dan kalender Hijriah',
        'Jaringan pencatat waktu tersinkron GPS untuk merekam kontak gerhana di setiap stasiun',
        'Panduan waktu shalat kusuf di sepanjang jalur gerhana',
      ],
      en: [
        'Public module on astronomical timekeeping, hisab-rukyat, and the Hijri calendar',
        'GPS-synchronised timing network to record eclipse contacts at every station',
        'Guide to eclipse prayer (shalat kusuf) times along the eclipse path',
      ],
    },
    leadPerson: null,
    leadOrganization: null,
    status: 'draft',
  },
  {
    slug: 'open-education',
    order: 2,
    ordinal: { id: 'Cahaya Kedua', en: 'Second Light' },
    title: { id: 'Pendidikan Terbuka', en: 'Open Education' },
    summary: {
      id: 'Gerakan membuka akses ilmu seluas-luasnya: materi gerhana dan astronomi yang gratis, berlisensi terbuka, dan siap dipakai sekolah, pesantren, serta komunitas.',
      en: 'A movement to open access to knowledge as widely as possible: eclipse and astronomy materials that are free, openly licensed, and ready for schools, pesantren, and communities.',
    },
    deliverables: {
      id: [
        'Kurikulum dan lembar kerja gerhana berlisensi terbuka untuk sekolah dan pesantren',
        'Pelatihan guru dan fasilitator pengamatan gerhana',
        'Kelas daring terbuka tentang Matahari, Bulan, dan gerhana',
      ],
      en: [
        'Openly licensed eclipse curriculum and worksheets for schools and pesantren',
        'Training for teachers and eclipse-observation facilitators',
        'Open online course on the Sun, the Moon, and eclipses',
      ],
    },
    leadPerson: null,
    leadOrganization: null,
    status: 'draft',
  },
  {
    slug: 'science-entertainment',
    order: 3,
    ordinal: { id: 'Cahaya Ketiga', en: 'Third Light' },
    title: { id: 'Hiburan Sains', en: 'Science Entertainment' },
    summary: {
      id: 'Gerakan menjadikan sains menyenangkan dan dekat dengan publik melalui tontonan, cerita, dan pertunjukan yang membangkitkan rasa ingin tahu.',
      en: 'A movement to make science enjoyable and close to the public through shows, stories, and performances that spark curiosity.',
    },
    deliverables: {
      id: [
        'Siaran langsung totalitas dari Makkah dan stasiun pendukung',
        'Film dokumenter perjalanan Ekspedisi 7 Cahaya',
        'Seri video pendek, podcast, dan pertunjukan sains keliling',
      ],
      en: [
        'Live broadcast of totality from Makkah and supporting stations',
        'Documentary film of the 7 Lights Expedition journey',
        'Short-video series, podcast, and touring science shows',
      ],
    },
    leadPerson: null,
    leadOrganization: null,
    status: 'draft',
  },
  {
    slug: 'science-tourism',
    order: 4,
    ordinal: { id: 'Cahaya Keempat', en: 'Fourth Light' },
    title: { id: 'Wisata Sains', en: 'Science Tourism' },
    summary: {
      id: 'Gerakan perjalanan yang memadukan ibadah, budaya, dan sains — mengajak peserta menyaksikan gerhana langsung di jalur totalitas dengan aman dan bermakna.',
      en: 'A movement of travel that brings together worship, culture, and science — inviting participants to witness the eclipse on the path of totality, safely and meaningfully.',
    },
    deliverables: {
      id: [
        'Paket perjalanan ekspedisi gerhana bersama mitra perjalanan',
        'Lokasi pengamatan terpandu di sepanjang jalur totalitas',
        'Panduan keselamatan mata dan perjalanan bagi peserta',
      ],
      en: [
        'Eclipse-expedition travel packages with travel partners',
        'Guided observation sites along the path of totality',
        'Eye-safety and travel guide for participants',
      ],
    },
    leadPerson: null,
    leadOrganization: null,
    status: 'draft',
  },
  {
    slug: 'gravitational-lensing',
    order: 5,
    ordinal: { id: 'Cahaya Kelima', en: 'Fifth Light' },
    title: { id: 'Eksperimen Pelensaan Gravitasi', en: 'Gravitational Lensing Experiment' },
    summary: {
      id: 'Gerakan riset untuk mengulang uji klasik relativitas umum: mengukur pembelokan cahaya bintang oleh gravitasi Matahari selama totalitas, dengan instrumen dan metode modern.',
      en: 'A research movement to repeat a classic test of general relativity: measuring how the Sun’s gravity bends starlight during totality, using modern instruments and methods.',
    },
    deliverables: {
      id: [
        'Protokol pengamatan dan rancangan instrumen untuk pencitraan medan bintang saat totalitas',
        'Citra medan bintang di sekitar Matahari dari stasiun Makkah',
        'Dataset terbuka dan makalah analisis pembelokan cahaya',
      ],
      en: [
        'Observation protocol and instrument design for star-field imaging during totality',
        'Star-field images around the Sun from the Makkah station',
        'Open dataset and analysis paper on light deflection',
      ],
    },
    leadPerson: null,
    leadOrganization: null,
    status: 'draft',
  },
  {
    slug: 'instrumentation-robotics-automation',
    order: 6,
    ordinal: { id: 'Cahaya Keenam', en: 'Sixth Light' },
    title: {
      id: 'Instrumentasi, Robotika & Otomasi',
      en: 'Instrumentation, Robotics & Automation',
    },
    summary: {
      id: 'Gerakan membangun kemandirian teknologi: merancang dan merakit sendiri instrumen, sensor, dan sistem otomatis yang menjalankan pengamatan gerhana.',
      en: 'A movement for technological self-reliance: designing and building our own instruments, sensors, and automated systems to run eclipse observations.',
    },
    deliverables: {
      id: [
        'Dudukan teleskop dan kamera otomatis yang dirakit tim sendiri',
        'Kit sensor lingkungan untuk suhu, cahaya, dan tekanan selama gerhana',
        'Desain perangkat keras dan perangkat lunak terbuka yang dapat direplikasi',
      ],
      en: [
        'In-house automated telescope and camera mounts',
        'Environmental sensor kits for temperature, light, and pressure during the eclipse',
        'Open hardware and software designs that others can replicate',
      ],
    },
    leadPerson: null,
    leadOrganization: null,
    status: 'draft',
  },
  {
    slug: 'islamic-finance-for-science',
    order: 7,
    ordinal: { id: 'Cahaya Ketujuh', en: 'Seventh Light' },
    title: { id: 'Keuangan Islam untuk Sains', en: 'Islamic Finance for Science' },
    summary: {
      id: 'Gerakan membiayai ilmu pengetahuan melalui instrumen keuangan syariah — wakaf, sedekah, dan pendanaan bersama — secara transparan dan berkelanjutan melampaui 2027.',
      en: 'A movement to fund science through Islamic finance instruments — waqf, charity, and collective funding — transparently and sustainably beyond 2027.',
    },
    deliverables: {
      id: [
        'Skema wakaf sains untuk pendanaan program dan instrumen',
        'Platform pendanaan bersama sesuai syariah untuk ekspedisi',
        'Laporan keuangan terbuka dan model pembiayaan riset untuk program berikutnya',
      ],
      en: [
        'Science waqf scheme to fund the programme and its instruments',
        'Sharia-compliant collective funding platform for the expedition',
        'Open financial reporting and a research-funding model for future programmes',
      ],
    },
    leadPerson: null,
    leadOrganization: null,
    status: 'draft',
  },
]

export const HAS_DRAFT_PILLARS = CAHAYA_PILLARS.some((pillar) => pillar.status === 'draft')
